import { useCallback, useState } from "react";
import {
  fetchCustomerProfileByPhone,
  fetchCustomerProfileByIdentity,
  createCustomer,
  updateCustomer,
  searchCustomers,
} from "../../models/shared/customersModel";

export function useCustomerProfile() {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadByPhone = useCallback(async (phone) => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchCustomerProfileByPhone(phone);
      setCustomer(result);
      return result;
    } catch (e) {
      setError(e.message);
      // A transport/permission failure is not the same business result as a
      // successful lookup with no customer. Callers use null to decide whether
      // to create a customer, so swallowing this error could trigger a bogus
      // create attempt while the network is down.
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadByIdentity = useCallback((identity) => fetchCustomerProfileByIdentity(identity), []);

  const create = useCallback(async (payload, { requireSuccess = true } = {}) => {
    const res = await createCustomer(payload);
    // The header's existing auto-registration flow reloads after any resolved
    // response; preserve that contract without changing other callers.
    if (requireSuccess && !res.success) throw new Error(res.message);
    return res;
  }, []);

  const update = useCallback(async (payload) => {
    const res = await updateCustomer(payload);
    if (!res.success) throw new Error(res.message);
    setCustomer(res.data);
    return res;
  }, []);

  const search = useCallback((term) => searchCustomers(term), []);

  return { customer, loading, error, loadByPhone, loadByIdentity, create, update, search, setCustomer };
}
